import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import Button from "@/common/elements/Button";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import { Popconfirm } from "antd";
import BookingQueueDetailForm from "./BookingQueueDetails/BookingQueueDetailForm";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
} from "@/util/common_function";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import notificationService from "@/util/notificationService";
import API_ENDPOINTS from "@/util/api";
import { TableHeader } from "../InmateDetails/styles";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const ReleaseQueueDetails = () => {
  const dispatch = useDispatch();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state.bookings
  );
  const releaseQueueDetails = bookings?.releaseQueueDetails || [];

  const [showReleaseQueueDetails, setShowReleaseQueueDetails] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef();

  const releaseQueueSteps = getOptionsFromRefList(
    "ReleaseQueueStep",
    false,
    false,
    true
  )?.slice(1);

  // Create a mapping of step IDs to their names
  const stepNameMap = releaseQueueSteps.reduce((acc, step) => {
    acc[step.value] = step.label;
    return acc;
  }, {});

  const [persistedMapping, setPersistedMapping] = useState({});
  useEffect(() => {
    const newMapping = {};
    releaseQueueDetails.forEach((detail) => {
      if (detail.releaseQueueDetailID) {
        newMapping[detail.releaseQueStepID] = detail.releaseQueueDetailID;
      }
    });
    setPersistedMapping(newMapping);
  }, [releaseQueueDetails]);

  const handleCheckboxChange = (step, checked) => {
    const isStepAlreadyAdded = releaseQueueDetails.some(
      (item) => item.releaseQueStepID === step.value
    );

    if (checked) {
      const modalPayload = {
        releaseQueStepID: step.value,
        stepName: step.label,
        complete: true,
        completionStaffID: userDetails?.staffId,
      };
      if (persistedMapping[step.value]) {
        modalPayload.releaseQueueDetailID = persistedMapping[step.value];
      }
      setModalData(modalPayload);
      setIsEditing(false);
      setShowReleaseQueueDetails(true);
    } else if (isStepAlreadyAdded) {
      // When unchecking, show confirmation pop-up
      return (
        <Popconfirm
          title="Are you sure you want to permanently delete this step?"
          description={
            <>
              <div>
                Un-checking this box will permanently remove the Step from the
                Queue.
              </div>
              <div>
                If you want to mark the step as incomplete, click Cancel, then
                select the step and click the Details button instead.
              </div>
            </>
          }
          okText="Yes, Delete"
          cancelText="Cancel"
          onConfirm={() => {
            // If a persisted mapping exists, call the delete API
            if (persistedMapping[step.value]) {
              const url =
                API_ENDPOINTS.releaseQueueDetails +
                "/" +
                persistedMapping[step.value];
              dispatch(setDeleteAPis(url));
            }
            // Update the bookings state to remove the step
            const updatedDetails = releaseQueueDetails.filter(
              (item) => item.releaseQueStepID !== step.value
            );
            dispatch(
              setBookings({
                ...bookings,
                releaseQueueDetails: updatedDetails,
              })
            );
          }}
        >
          <Checkbox checked={true} disabled={!bookingsFieldsEnabled} />
        </Popconfirm>
      );
    }
  };

  const handleSave = async () => {
    const formData = await formRef.current.getFieldsValue();

    const sanitizedData = {
      releaseQueueDetailID:
        formData?.releaseQueueDetailID ||
        `new-${releaseQueueDetails.length + 1}`,
      releaseQueStepID: formData?.releaseQueStepID,
      completionDate: formData?.completionDate,
      completionTime: formData?.completionTime,
      creatorStaffID: userDetails?.staffId,
      completionStaffID: formData?.completionStaffID,
      comments: formData?.comments,
      complete: formData?.complete || false,
    };

    if (
      typeof sanitizedData.releaseQueueDetailID === "string" &&
      sanitizedData.releaseQueueDetailID?.includes("new")
    ) {
      delete sanitizedData.releaseQueueDetailID;
    }

    if (isEditing) {
      const updatedDetails = releaseQueueDetails.map((item) =>
        (item.releaseQueueDetailID &&
          item.releaseQueueDetailID === formData.releaseQueueDetailID) ||
        item.releaseQueStepID === formData.releaseQueStepID
          ? sanitizedData
          : item
      );

      dispatch(
        setBookings({
          ...bookings,
          releaseQueueDetails: updatedDetails,
        })
      );
    } else {
      const updatedDetails = [...releaseQueueDetails, sanitizedData];
      dispatch(
        setBookings({
          ...bookings,
          releaseQueueDetails: updatedDetails,
        })
      );
    }

    setShowReleaseQueueDetails(false);
    setModalData(null);
    setIsEditing(false);
  };

  const handleAdd = () => {
    setModalData(null);
    setIsEditing(false);
    setShowReleaseQueueDetails(true);
  };

  const handleEdit = () => {
    if (!modalData) {
      notificationService.warning("Please select a row to edit.");
      return;
    }
    setIsEditing(true);
    setShowReleaseQueueDetails(true);
  };

  const handleDelete = () => {
    if (!modalData) {
      notificationService.warning("Please select a row to delete.");
      return;
    }

    const updatedDetails = releaseQueueDetails.filter(
      (item) => item.releaseQueueDetailID !== modalData.releaseQueueDetailID
    );

    dispatch(
      setBookings({
        ...bookings,
        releaseQueueDetails: updatedDetails,
      })
    );

    setModalData(null);
  };

  const handleRowClick = (row) => {
    setModalData(row);
  };

  const ReleaseQueueDetailsTable = {
    headers: [
      { key: "displayReleaseQueueDetailID", text: "ID" },
      { key: "displayName", text: "Step" },
      { key: "displayComplete", text: "Complete" },
      { key: "completionDate", text: "Completion Date" },
      { key: "comments", text: "Comments" },
    ],
    rows: releaseQueueDetails.map((row, index) => ({
      ...row,
      releaseQueueDetailID: row.releaseQueueDetailID || `new-${index + 1}`,
      displayReleaseQueueDetailID: row.releaseQueueDetailID || "",
      displayName: stepNameMap[row.releaseQueStepID] || row.releaseQueStepID,
      displayComplete: row.complete ? "True" : "False",
    })),
  };

  return (
    <>
      <StyledGridRow gutter={16}>
        {releaseQueueSteps.map((step) => {
          const isChecked = releaseQueueDetails.some(
            (detail) =>
              detail.releaseQueStepID === step.value && detail.complete === true
          );

          return (
            <StyledGridCol xs={7} xl={7} xxl={6} key={step.value}>
              <FormItem
                label={step.label}
                layout="horizontal"
                labelPostion="label-right"
              >
                {isChecked ? (
                  <Popconfirm
                    title="Are you sure you want to permanently delete this step?"
                    description={
                      <>
                        <div>
                          Un-checking this box will permanently remove the Step
                          from the Queue.
                        </div>
                        <div>
                          If you want to mark the step as incomplete, click
                          Cancel, then select the step and click the Details
                          button instead.
                        </div>
                      </>
                    }
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    onConfirm={() => {
                      // If a persisted mapping exists, call the delete API
                      if (persistedMapping[step.value]) {
                        const url =
                          API_ENDPOINTS.releaseQueueDetails +
                          "/" +
                          persistedMapping[step.value];
                        dispatch(setDeleteAPis(url));
                      }
                      const updatedDetails = releaseQueueDetails.filter(
                        (item) => item.releaseQueStepID !== step.value
                      );
                      dispatch(
                        setBookings({
                          ...bookings,
                          releaseQueueDetails: updatedDetails,
                        })
                      );
                    }}
                  >
                    <Checkbox checked={true} />
                  </Popconfirm>
                ) : (
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) =>
                      handleCheckboxChange(step, e.target.checked)
                    }
                    disabled={!bookingsFieldsEnabled}
                  />
                )}
              </FormItem>
            </StyledGridCol>
          );
        })}
      </StyledGridRow>

      {showReleaseQueueDetails && (
        <Modal
          header={"Release Queue Details"}
          show={showReleaseQueueDetails}
          size="xl"
          onHide={() => {
            setModalData(null);
            setShowReleaseQueueDetails(false);
          }}
          footerActions={[
            {
              key: "save",
              text: "Save",
              onClick: handleSave,
            },
          ]}
          footerCloseAction
          footerCloseActionText="Close"
        >
          <BookingQueueDetailForm
            ref={formRef}
            stepData={modalData}
            isEditing={isEditing}
            existingSteps={releaseQueueDetails.map(
              (item) => item.releaseQueStepID
            )}
            queueType="releaseQueue"
            onCloseModal={() => setShowReleaseQueueDetails(false)}
          />
        </Modal>
      )}

      <TableHeader style={{ marginTop: "18px", border: "none" }}>
        <h6 style={{ margin: "0px" }}>Release Queue Details </h6>
        <div>
          <StyledGridRow gutter={16}>
            <StyledGridCol>
              <Button
                onClick={handleAdd}
                disabled={
                  !bookingsFieldsEnabled ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.ADD,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report
                  )
                }
              >
                Add
              </Button>
            </StyledGridCol>
            {releaseQueueDetails.length > 0 && (
              <StyledGridCol>
                <Button
                  onClick={handleEdit}
                  disabled={
                    !modalData ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.VIEW,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  Details
                </Button>
              </StyledGridCol>
            )}
          </StyledGridRow>
        </div>
      </TableHeader>
      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          selected={!modalData}
          headers={ReleaseQueueDetailsTable.headers}
          data={ReleaseQueueDetailsTable.rows}
          onRowClick={handleRowClick}
        />
      )}
    </>
  );
};

export default ReleaseQueueDetails;
